import React from "react";

import PartnerAddEmployeeForm from "EmployerApplication/Components/PartnerAddEmployeeForm";
import { canLinkAccount } from "EmployerApplication/Selectors";
import { api } from "EmployerApplication/urls";
import createDecorator from "final-form-focus";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";
import { defaultHandler } from "Utils/http";

import { UPLOADED_EMPLOYEE, UPLOAD_EMPLOYEE_ERROR } from "../Actions";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "Cobalt/Modal";
import Button from "Common/Button";
import SubmissionErrorWrapper from "Common/forms/SubmissionErrorWrapper";
import Header from "Cobalt/Header";
import Icon from "Common/Icon";

const focusOnErrors = createDecorator();

const PartnerAddEmployeeModal = ({ onHide, show, include1099, includeOwner }) => {
  const offerLinkAccount = useSelector(canLinkAccount);
  const dispatch = useDispatch();

  const onSubmit = async (values) => {
    const {
      employee,
      employee: { full_name, employee_type, employee_fill },
    } = values;
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "add_employee_modal",
      label: "add employee",
    });

    AnalyticsTracking.trackEvent("add_new_employee_data", {
      employee_name: full_name,
      employee_type,
      employee_fill,
    });

    try {
      const response = await fetchWithCSRFToken(api.addEmployee, {
        method: "POST",
        body: JSON.stringify({
          ...employee,
          company_id_token: companyToken(),
        }),
      });

      await defaultHandler(response);
      const json = await response.json();
      dispatch({ type: UPLOADED_EMPLOYEE, employee: json });
      onHide();
    } catch (e) {
      dispatch({
        type: UPLOAD_EMPLOYEE_ERROR,
        error: `${e.name} - ${e.statusCode}`,
      });

      new SubmissionErrorWrapper(e)
        .forForbiddenError(() => {
          window.location.href = "/unexpected-error";
        })
        .orRethrow();
    }
  };
  const handleClose = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "add_employee_modal",
      label: "cancel",
    });

    onHide();
  };

  const fallBackName = () => {
    if (include1099 && includeOwner) {
      return "Owner or Contractor";
    }
    if (include1099 && !includeOwner) {
      return "Contractor";
    }
    if (!include1099 && includeOwner) {
      return "Owner";
    }
    return "Employee";
  };

  const initialValues = !include1099 ? { employee: { employee_type: "owner" } } : !includeOwner ? { employee: { employee_type: "1099" } } : {};

  return (
    <Modal show={show} onHide={handleClose} className="AddEmployeeModal">
      <Form onSubmit={onSubmit} decorators={[focusOnErrors]} initialValues={initialValues}>
        {({ handleSubmit, invalid, submitting, values }) => {
          const { employee: { employee_fill, full_name } = {} } = values;
          const person = full_name ? full_name.replace(/^\s+|\s+$/g, "") : fallBackName();
          return (
            <form onSubmit={handleSubmit}>
              <ModalHeader closeButton>
                <Header level={7} mb={0}>
                  Add {person}
                </Header>
              </ModalHeader>
              <ModalBody>
                <PartnerAddEmployeeForm
                  employeeFullName={person}
                  employeeWillFillApplication={employee_fill}
                  canLinkAccount={offerLinkAccount}
                  showOptionW2={false}
                  showOption1099={include1099}
                  showOptionOwner={includeOwner}
                />
              </ModalBody>
              <ModalFooter>
                <Button color="gray" onClick={handleClose} disabled={submitting} mr={3}>
                  Cancel
                </Button>
                <Button id="addButton" type="submit" disabled={invalid || submitting} aria-label="Confirm add employee">
                  {submitting ? (
                    <>
                      <Icon icon="cog" spin mr={3} flavor="solid" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Icon className="AddEmployeeModal__addIcon" icon="plus" mr={3} />
                      Add {person}
                    </>
                  )}
                </Button>
              </ModalFooter>
            </form>
          );
        }}
      </Form>
    </Modal>
  );
};

export default PartnerAddEmployeeModal;
